/**
 * When the app was last in use, read once before this session overwrites it.
 *
 * The value has to be captured at import: every hook that restores something needs to know how
 * long the app had been shut, and by the time the first of them runs the stamp is already being
 * refreshed. One stamp for the whole app, because "he has not opened it in an hour" is one fact
 * about the phone rather than one per stored thing.
 */

import { useEffect } from "react";

const STORAGE_KEY = "gotime.lastSeen";

const TOUCH_INTERVAL_MS = 60_000;

function read(): number | null {
	try {
		const raw = localStorage.getItem(STORAGE_KEY);
		const parsed = raw == null ? Number.NaN : Number(raw);
		return Number.isFinite(parsed) ? parsed : null;
	} catch {
		return null;
	}
}

const previousMs = read();

/** How long ago the app was last used, from before this session started. */
export function lastSeenMs(): number | null {
	return previousMs;
}

export function touchLastSeen(): void {
	try {
		localStorage.setItem(STORAGE_KEY, String(Date.now()));
	} catch {
		// Private browsing; nothing will be sticky, which is the safe way to be wrong.
	}
}

/**
 * Keep the stamp fresh for as long as the app is on screen.
 *
 * Marked on the way out as well as while visible: iOS suspends a hidden PWA without warning, so
 * the last mark before it goes is the one that decides whether the next open is the same session.
 */
export function useLastSeen(): void {
	useEffect(() => {
		touchLastSeen();
		const timer = window.setInterval(() => {
			if (document.visibilityState === "visible") {
				touchLastSeen();
			}
		}, TOUCH_INTERVAL_MS);
		document.addEventListener("visibilitychange", touchLastSeen);
		return () => {
			window.clearInterval(timer);
			document.removeEventListener("visibilitychange", touchLastSeen);
		};
	}, []);
}
